import * as React from 'react';
import { ITenantConfig } from 'data/tenant-config';
import { sendApiRequest } from 'utils/network';
import { brandNames, marketSchema } from 'data/schema';
import { ITenant } from './types';
import { TenantHelpBanner } from './TenantHelpBanner';

export function NewMarketForm({
  onCancel,
  onCreateTenant,
  defaultBrand,
  defaultMarket,
  defaultTenant,
  requireTenant,
  tenants,
}: {
  onCancel: () => void;
  onCreateTenant: () => void;
  defaultBrand?: string;
  defaultMarket?: string;
  defaultTenant?: string;
  requireTenant?: boolean;
  tenants: ITenant[];
}) {
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedTenant, setSelectedTenant] = React.useState<string>(defaultTenant ?? (requireTenant ? '' : '*'));

  const isTenantEmpty = requireTenant && !selectedTenant;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (isTenantEmpty) return;

    const formData = new FormData(event.currentTarget);

    setIsSaving(true);

    try {
      const body = Object.fromEntries(formData.entries());
      const response = await sendApiRequest<ITenantConfig>('/config', {
        method: 'POST',
        body,
      });
      if (response) {
        setError(null);
        window.location.hash = response.id;
        window.location.reload();
      }
    } catch (error) {
      console.error('Failed to create market:', error);
      setError((error as Error).message ?? error);
    }

    setIsSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className=" mx-auto mb-3 max-w-7xl rounded-md bg-teal-dark px-1 py-3 sm:px-2 lg:px-4">
      <div className="space-y-8">
        <div className="border-b border-gray-10050 pb-12">
          <h2 className="text-base/7 font-semibold text-white">New Market information</h2>
          <p className="mt-1 text-sm/6 text-gray-400">
            {requireTenant
              ? 'Create a tenant-specific configuration for this brand-market combination.'
              : 'Create a new configuration for a brand-market combination. Optionally, select a tenant to create config just for that tenant.'}
          </p>

          <div className="mt-6">
            <TenantHelpBanner />
          </div>

          <div className="mt-4 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
            <div className="sm:col-span-2">
              <label htmlFor="brand" className="block text-sm/6 font-medium text-white">
                Brand
              </label>
              <div className="mt-2 grid grid-cols-1">
                <select
                  id="brand"
                  name="brand"
                  defaultValue={defaultBrand}
                  className="bg-white/5 outline-white/10 col-start-1 row-start-1 w-full appearance-none rounded-md py-1.5 pl-3 pr-8 text-base text-black outline outline-1 -outline-offset-1 *:bg-gray-900 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-teal-dark sm:text-sm/6"
                >
                  {Object.entries(brandNames).map(([code, brandName]) => (
                    <option key={code} value={code}>
                      {brandName}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="market" className="block text-sm/6 font-medium text-white">
                Market
              </label>
              <div className="mt-2">
                <select
                  id="market"
                  name="market"
                  defaultValue={defaultMarket}
                  className="bg-white/5 outline-white/10 col-start-1 row-start-1 w-full appearance-none rounded-md py-1.5 pl-3 pr-8 text-base text-black outline outline-1 -outline-offset-1 *:bg-gray-900 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-teal-dark sm:text-sm/6"
                >
                  {marketSchema.options.map((market) => (
                    <option key={market} value={market}>
                      {market}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="sm:col-span-2">
              <label htmlFor="tenant" className="block text-sm/6 font-medium text-white">
                Tenant <span className="text-sm/6 font-normal text-gray-400">(Specific integrators only)</span>
              </label>
              <div className="mt-2">
                <select
                  id="tenant"
                  name="tenant"
                  value={selectedTenant}
                  onChange={(e) => setSelectedTenant(e.target.value)}
                  className="bg-white/5 outline-white/10 col-start-1 row-start-1 w-full appearance-none rounded-md py-1.5 pl-3 pr-8 text-base text-black outline outline-1 -outline-offset-1 *:bg-gray-900 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-teal-dark sm:text-sm/6"
                >
                  {requireTenant ? (
                    <option value="" disabled>
                      Select a tenant…
                    </option>
                  ) : (
                    <option value="*">All tenants</option>
                  )}
                  {tenants.map((tenant) => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                className="mt-2 text-sm font-medium text-teal hover:text-teal-700"
                onClick={onCreateTenant}
              >
                + New Tenant
              </button>
            </div>
          </div>
        </div>
        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="shrink-0">
                <svg
                  className="size-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                  data-slot="icon"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM8.28 7.22a.75.75 0 0 0-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 1 0 1.06 1.06L10 11.06l1.72 1.72a.75.75 0 1 0 1.06-1.06L11.06 10l1.72-1.72a.75.75 0 0 0-1.06-1.06L10 8.94 8.28 7.22Z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">There was an error with your submission</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-6 flex items-center justify-end gap-x-6">
        <button
          type="button"
          data-testid="new-market-cancel-button"
          className="text-sm/6 font-semibold text-white disabled:opacity-75"
          disabled={isSaving}
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="submit"
          data-testid="new-market-save-button"
          disabled={isSaving || !!isTenantEmpty}
          className="rounded-md bg-teal px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-dark enabled:hover:bg-green-700 disabled:opacity-75"
        >
          Save
        </button>
      </div>
    </form>
  );
}
